import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FollowButton } from "scmp-sdk/components/follow-button";
import { RedirectButton } from "scmp-sdk/components/redirect-button";

import { fontMerriweather, fontRoboto, fontRobotoCondensed, theme } from "shared/lib/styles";

export const Image = styled.img`
  inline-size: 100%;
  block-size: 100%;

  object-fit: cover;
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;

  inline-size: 100%;

  > * + * {
    margin-block-start: 12px;

    ${theme.breakpoints.up("tablet")} {
      margin-block-start: 8px;
    }
  }
`;

export const Title = styled.div`
  margin-block-start: 0;

  text-align: center;

  ${theme.breakpoints.up("tablet")} {
    margin-inline: 20px;
  }
`;

export const Description = styled.div`
  font-family: ${fontRoboto};

  ${theme.breakpoints.up("tablet")} {
    margin-inline: 20px;
  }

  span {
    font-weight: 700;
  }
`;

export const TagLine = styled.div`
  max-inline-size: 240px;
  margin-block-end: 8px;
  padding-block: 4px;
  padding-inline: 8px;

  color: #001246;
  font-weight: 700;
  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  text-align: center;

  background-color: #ffca05;

  ${theme.breakpoints.up("mobile")} {
    font-size: 16px;
  }
`;

type Props = {
  $variant?: "default" | "soft";
};

export const StyledRedirectButton = styled(RedirectButton)`
  font-size: 14px;
`;

export const StyledFollowButton = styled(FollowButton)`
  inline-size: 108px;
  padding-block: 10px;
  padding-inline: 20px;

  font-size: 14px;
  line-height: normal;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 94px;
    padding-block: 8px;
    padding-inline: 0;

    line-height: 14px;
  }
`;

export const Container = styled.div<Props>`
  position: relative;

  display: grid;

  margin: auto;

  outline: none;

  ${Image} {
    grid-area: image;
  }

  ${ContentContainer} {
    grid-area: content;
  }

  ${props => {
    switch (props.$variant) {
      case "soft":
        return css`
          grid:
            "content" min-content
            "image" min-content /
            100%;

          inline-size: calc(100% - 40px);

          background: #f6f6f6;

          ${theme.breakpoints.up("mobile")} {
            max-inline-size: 368px;
            max-block-size: 670px;
          }

          ${theme.breakpoints.up("tablet")} {
            max-inline-size: 600px;
            max-block-size: 729px;
          }

          ${ContentContainer} {
            padding-block: 0 12px;
          }

          ${Title} {
            color: #001246;
            font-weight: 700;
            font-size: 22px;
            font-family: ${fontRoboto};
            line-height: 33px;
            text-align: center;

            ${theme.breakpoints.up("mobile")} {
              font-size: 28px;
            }
          }

          ${Description} {
            inline-size: 252px;

            color: #000000;
            font-size: 14px;
            line-height: 18px;
            text-align: center;

            ${theme.breakpoints.up("tablet")} {
              inline-size: 400px;
            }
          }

          ${Image} {
            block-size: 169px;
            padding: 4px;

            ${theme.breakpoints.up("tablet")} {
              block-size: 338px;
            }
          }
        `;
      default:
        return css`
          grid:
            "image" min-content
            "content" min-content /
            100%;

          max-inline-size: calc(100% - 40px);

          background: #001246;

          ${theme.breakpoints.up("mobile")} {
            max-inline-size: 335px;
            max-block-size: 670px;
          }

          ${theme.breakpoints.up("tablet")} {
            max-inline-size: 600px;
            max-block-size: 729px;
          }

          ${ContentContainer} {
            padding-block: 16px;
            padding-inline: 20px;

            ${theme.breakpoints.up("tablet")} {
              min-block-size: 203px;
              padding-block: 0;
              padding-inline: 0;
            }
          }

          ${Title} {
            color: #ffca05;
            font-weight: 400;
            font-size: 16px;
            font-family: ${fontMerriweather};
            line-height: normal;

            ${theme.breakpoints.up("tablet")} {
              font-size: 20px;
            }
          }

          ${Description} {
            color: #ffffff;
            font-weight: 400;
            font-size: 12px;
            line-height: normal;
            text-align: center;

            ${theme.breakpoints.up("tablet")} {
              font-size: 16px;
            }
          }

          ${TagLine} {
            display: none;
          }

          ${Image} {
            block-size: 187px;

            ${theme.breakpoints.down("mobile")} {
              block-size: 187px;
            }

            ${theme.breakpoints.up("mobile")} {
              block-size: 224px;
            }

            ${theme.breakpoints.up("tablet")} {
              block-size: 400px;
            }
          }

          ${StyledRedirectButton} {
            margin-block-start: 24px;
          }

          ${StyledFollowButton} {
            margin-block-start: 12px;

            ${theme.breakpoints.up("tablet")} {
              margin-block-start: 24px;
            }
          }
        `;
    }
  }}

  ${theme.breakpoints.up("tablet")} {
    inline-size: 600px;
    max-block-size: 729px;
  }
`;

export const Formatter = styled.div`
  color: #ffca05;
  font-weight: 500;
  font-size: 18px;
  font-family: ${fontMerriweather};
  line-height: 23px;
`;
